/**
 * `useBrand()` (TMMAPP-0035, TMMAPP-0042, TMMAPP-0048) — the active brand theme's assets and
 * localised copy.
 *
 * - `source: "brand"` — the tenant's identity: its copy and assets; a `null` product name / short
 *   name / welcome headline falls back to the catalog (`brand.*`) so a string never renders empty.
 * - `source: "platform-default"` — the look, plus only the **product's** `productName` /
 *   `productShortName`, which saas-auth serves on a tenant host (TMMAPP-0048). Each is used when
 *   non-blank after trimming, else the catalog key. Every other copy key and all assets come from
 *   us: the catalog keys and the built-in mark / favicon (TMMAPP-0042).
 * - A name equal to the built-in `PLATFORM_DEFAULT_THEME`'s (saas-auth's no-tenant document, whose
 *   copy is the platform's, never the product's) is ignored too. That covers the built-in itself
 *   and a pre-SASATH-0352 cached tenant document, which carried the platform copy.
 *
 * Product names are trimmed on every path (a stored brand name can carry a trailing space).
 */
import { useTranslation } from 'react-i18next';
import { useBrandTheme } from '@/context/brand-theme-context';
import { DEFAULT_LOCALE, normalizeLocale } from '@/i18n/constants';
import { useLocale } from '@/i18n/useLocale';
import type { BrandAssets, BrandCopyStrings, BrandTheme } from './brandTheme';
import { PLATFORM_DEFAULT_THEME } from './platformDefaultTheme';

export interface BrandIdentity {
  assets: BrandAssets;
  /** The brand's copy strings for the resolved locale, `null` where the brand has none. */
  copy: BrandCopyStrings;
  productName: string;
  productShortName: string;
  welcomeHeadline: string;
  /** The brand's light-mode surface — the chip a non-reversed logo sits on over a fill. */
  lightSurface: string;
}

const EMPTY_COPY: BrandCopyStrings = {
  productName: null,
  productShortName: null,
  loginTitle: null,
  tagline: null,
  heroCopy: null,
  location: null,
  welcomeHeadline: null,
  supportEmail: null,
};

/** Built-in identity assets — used whenever `source === 'platform-default'`. */
export const APP_IDENTITY_ASSETS: BrandAssets = {
  logo: null,
  logoReversed: null,
  mark: '/brand/money-meeting-mark.svg',
  favicon: { svg: '/favicon.svg', png: null },
  appleTouchIcon: null,
  maskableIcon: null,
  hero: null,
  onboardingIllustration: { light: null, dark: null },
  emailHeader: null,
  socialImage: null,
};

export function localizedCopy(theme: BrandTheme, locale: string): BrandCopyStrings {
  const { locales, defaultLocale } = theme.copy;
  return locales[locale] ?? locales[defaultLocale] ?? EMPTY_COPY;
}

/** The copy `useBrand()` honours: all of a brand's, only the two names of a platform-default. */
function identityCopy(theme: BrandTheme, locale: string): BrandCopyStrings {
  if (theme.source !== 'platform-default') return localizedCopy(theme, locale);
  const copy = localizedCopy(theme, locale);
  const platform = localizedCopy(PLATFORM_DEFAULT_THEME, locale);
  const productOnly = (name: string | null, platformName: string | null) =>
    name?.trim() === platformName?.trim() ? null : name;
  return {
    ...EMPTY_COPY,
    productName: productOnly(copy.productName, platform.productName),
    productShortName: productOnly(copy.productShortName, platform.productShortName),
  };
}

export function useBrand(): BrandIdentity {
  const { t } = useTranslation();
  // Brand copy is keyed by the bare locale (`pt`), not the Intl tag `useLocale` returns.
  const locale = normalizeLocale(useLocale()) ?? DEFAULT_LOCALE;
  const { brandTheme } = useBrandTheme();
  const copy = identityCopy(brandTheme, locale);
  const assets = brandTheme.source === 'platform-default' ? APP_IDENTITY_ASSETS : brandTheme.assets;
  return {
    assets,
    copy,
    productName: copy.productName?.trim() || t('brand.productName'),
    productShortName: copy.productShortName?.trim() || t('brand.productShortName'),
    welcomeHeadline: copy.welcomeHeadline || t('brand.welcomeHeadline'),
    lightSurface: brandTheme.modes.light.surface,
  };
}
